import React, { useState } from 'react';
import {
  CheckCircle, Package, ChevronDown, ChevronRight,
  Pencil, Cigarette, Search, X, TrendingUp,
} from 'lucide-react';
import { ArticleRow } from './ArticleRow';

const catDisplay = { tabak: 'Tabak', shop: 'Shop', frische_backshop: 'Frische & Backshop' };

// ─── Paket-Karte ──────────────────────────────────────────────────────────────
export const PackageCard = ({ group, isOpen, onToggle, onCheck, updating, isCompleted, onEditColli, onToggleCategory, tobaccoPriceChanges = {}, outerHighlight = '' }) => {
  const { items, pak_number, category, isVirtual, colli_id } = group;
  const [itemSearch, setItemSearch] = useState('');

  // Pfand-Positionen ausblenden (nur sichtbare Artikel zählen/anzeigen)
  const visibleItems = items.filter(i => !i.is_pfand);
  const checked = visibleItems.filter(i => i.status === 'checked').length;
  const total = visibleItems.length;
  const allDone = total > 0 && checked === total;
  const isTabak = category === 'tabak';
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;

  // Anzahl NEUPREIS-Artikel in diesem Paket (nur für Tabak)
  // Lookup priorisiert fremd_article_number (so ist die changes-Map gekeyed), Fallback supplier_art_nr
  const newPriceCount = isTabak
    ? visibleItems.filter(i => tobaccoPriceChanges[i.fremd_article_number] || tobaccoPriceChanges[i.supplier_art_nr]).length
    : 0;

  // Gefilterte Artikel für Suche
  const filteredItems = itemSearch.trim()
    ? visibleItems.filter(i =>
        i.article_name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
        i.supplier_art_nr?.toString().includes(itemSearch)
      )
    : visibleItems;

  // Colli-ID als Hauptlabel, letzte 4 Ziffern hervorheben
  const colliLabel = colli_id || '';
  const colliSuffix = colliLabel.length >= 4 ? colliLabel.slice(-4) : colliLabel;
  const colliPrefix = colliLabel.length >= 4 ? colliLabel.slice(0, -4) : '';

  const label = isVirtual
    ? (catDisplay[category] || category)
    : colliLabel
      ? null  // Colli-ID wird separat gerendert
      : pak_number
        ? `PAK ${pak_number}`
        : (catDisplay[category] || 'Paket');

  return (
    <div
      className={`rounded-2xl border-2 transition-all duration-500 overflow-hidden ${
        allDone
          ? 'border-green-400 dark:border-green-500 bg-green-50/50 dark:bg-green-900/10 shadow-[0_0_12px_rgba(34,197,94,0.15)]'
          : isOpen
            ? 'border-primary/40 dark:border-primary/30 bg-white dark:bg-gray-900 shadow-md'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
      data-testid={`package-card-${pak_number || category}`}
    >
      {/* Header */}
      <button
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors"
        onClick={onToggle}
        data-testid={`package-toggle-${pak_number || category}`}
      >
        {/* Status-Icon */}
        {allDone ? (
          <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-green-500 flex items-center justify-center shadow-sm">
            <CheckCircle className="h-5 w-5 text-white" />
          </div>
        ) : (
          <div className={`flex-shrink-0 h-10 w-10 rounded-xl flex items-center justify-center shadow-sm ${isOpen ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
            <Package className="h-5 w-5" />
          </div>
        )}

        {/* Label + Progress */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            {colliLabel && !isVirtual ? (
              <span className={`font-mono tracking-wide flex items-baseline gap-0.5 ${allDone ? 'text-green-700 dark:text-green-300' : 'text-gray-900 dark:text-white'}`} data-testid={`colli-id-${pak_number}`}>
                {colliPrefix && <span className="text-[10px] opacity-30 font-normal leading-none">{colliPrefix}</span>}
                <span className="text-base font-bold">{colliSuffix}</span>
              </span>
            ) : (
              <span className={`text-sm font-bold ${allDone ? 'text-green-700 dark:text-green-300' : 'text-gray-900 dark:text-white'}`}>
                {label}
              </span>
            )}
            {!isVirtual && !isCompleted && onEditColli && (
              <span
                role="button"
                tabIndex={0}
                className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors"
                onClick={e => { e.stopPropagation(); onEditColli(group); }}
                data-testid={`edit-colli-${pak_number}`}
                title="Colli-ID bearbeiten"
              >
                <Pencil className="h-3 w-3" />
              </span>
            )}
            {!isVirtual && !isCompleted && onToggleCategory ? (
              <span
                role="button"
                tabIndex={0}
                onClick={e => { e.stopPropagation(); onToggleCategory(group); }}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors ${
                  isTabak
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/60'
                    : allDone
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
                title="Kategorie umschalten (Shop ↔ Tabak)"
                data-testid={`category-toggle-${pak_number || category}`}
              >
                {isTabak && <Cigarette className="h-3 w-3" />}
                {catDisplay[category] || category}
              </span>
            ) : (
              <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                isTabak
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                  : allDone
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {isTabak && <Cigarette className="h-3 w-3 mr-1 inline" />}
                {catDisplay[category] || category}
              </span>
            )}
            {pak_number && colliLabel && (
              <span className="text-xs text-gray-400 dark:text-gray-500">PAK {pak_number}</span>
            )}
            {/* NEUPREIS-Badge für Tabak-Pakete */}
            {isTabak && newPriceCount > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-bold bg-orange-500 text-white animate-pulse">
                <TrendingUp className="h-3 w-3" />
                {newPriceCount} NEUPREIS
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${allDone ? 'bg-green-500' : 'bg-primary'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className={`text-xs font-semibold whitespace-nowrap ${allDone ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {checked}/{total}
            </span>
          </div>
        </div>

        {/* Chevron */}
        <div className="flex-shrink-0">
          {isOpen
            ? <ChevronDown className="h-5 w-5 text-gray-400" />
            : <ChevronRight className="h-5 w-5 text-gray-400" />}
        </div>
      </button>

      {/* Artikel-Liste (expandierbar) */}
      {isOpen && (
        <div className="border-t border-gray-100 dark:border-gray-800 animate-in slide-in-from-top-2 duration-200">
          {/* Suchleiste */}
          {visibleItems.length > 4 && (
            <div className="px-3 pt-2.5 pb-1.5 sticky top-0 bg-white dark:bg-gray-900 z-10 border-b border-gray-100 dark:border-gray-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={itemSearch}
                  onChange={e => setItemSearch(e.target.value)}
                  placeholder="Artikel suchen…"
                  className="w-full pl-8 pr-8 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all placeholder:text-gray-400"
                  data-testid={`item-search-${pak_number || category}`}
                />
                {itemSearch && (
                  <button
                    onClick={() => setItemSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    data-testid={`item-search-clear-${pak_number || category}`}
                  >
                    <X className="h-3.5 w-3.5 text-gray-400" />
                  </button>
                )}
              </div>
              {itemSearch && (
                <p className="text-xs text-gray-400 mt-1 px-1">
                  {filteredItems.length} von {visibleItems.length} Artikeln
                </p>
              )}
            </div>
          )}
          {/* Artikel */}
          <div className="divide-y divide-gray-50 dark:divide-gray-800/50 px-1 pb-2">
            {filteredItems.length > 0 ? filteredItems.map(item => (
              <ArticleRow
                key={item.id}
                item={item}
                onCheck={onCheck}
                updating={updating}
                isCompleted={isCompleted}
                isTabak={isTabak}
                priceChange={isTabak ? (tobaccoPriceChanges[item.fremd_article_number] || tobaccoPriceChanges[item.supplier_art_nr] || null) : null}
                highlight={outerHighlight || itemSearch.trim()}
              />
            )) : (
              <p className="text-sm text-gray-400 text-center py-4">Kein Artikel gefunden</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
